import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthService } from '@/services/auth';
import { User, AuthConfig } from '@/types';
import { config } from '@/config';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  authService: AuthService | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authService, setAuthService] = useState<AuthService | null>(null);

  useEffect(() => {
    async function initAuth() {
      try {
        // Use config from build-time environment variables
        const authConfig: AuthConfig = {
          region: config.AWS_REGION,
          userPoolId: config.USER_POOL_ID,
          userPoolWebClientId: config.USER_POOL_CLIENT_ID,
          identityPoolId: config.IDENTITY_POOL_ID,
          apiEndpoint: config.API_ENDPOINT,
        };
        
        const service = new AuthService(authConfig);
        
        setAuthService(service);

        // Set up auth state listener
        service.addAuthStateListener((isAuth, currentUser) => {
          setIsAuthenticated(isAuth);
          setUser(currentUser);
        });

        // Check if user is already authenticated
        const isAuth = await service.isAuthenticated();
        if (isAuth) {
          setIsAuthenticated(true);
          const currentUser = await service.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();

    // Cleanup function
    return () => {
      if (authService) {
        authService.dispose();
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    if (!authService) throw new Error('Auth service not initialized');
    
    try {
      await authService.login(email, password);
      // Auth state will be updated through the Hub listener
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (!authService) return;
    
    try {
      await authService.logout();
      // Auth state will be updated through the Hub listener
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    authService,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}